/** Helper calculation functions */

const sumArrays = (...args) =>
  args.reduce((arr, current, i) =>
    arr.map((a, j) => a + current[j])
  )

const subtractArrays = (...args) =>
  args.reduce((arr, current, i) =>
    arr.map((a, j) => a - current[j])
  )

const calcDistance = ([x1, , y1], [x2, , y2]) => {
  let xs = x2 - x1,
    ys = y2 - y1
  xs *= xs
  ys *= ys
  const result = Math.sqrt(xs + ys)
  return result
}

/** Getter functions */

const getConnectionsForPieceId = (connections, pieceId) =>
  connections.filter(
    c => c.p1.id === pieceId || c.p2.id === pieceId
  )

const getGroupFromPieceId = (groups, pieceId) =>
  groups.find(g => getPieceInGroup(g, pieceId))

const getGroupFromId = (groups, groupId) =>
  groups.find(g => g.id === groupId)

const getPieceInGroup = (group, pieceId) =>
  group.pieces.find(m => m.id === pieceId)

/**
 * Gets all of the connections for a given group
 * @param {*} connections
 * @param {*} group
 */
const getConnectionsForGroup = (connections, group) => {
  const groupPieces = group.pieces.map(g => g.id)
  return connections.filter(
    c =>
      groupPieces.indexOf(c.p1.id) > -1 ||
      groupPieces.indexOf(c.p2.id) > -1
  )
}

const getPositionFromPieceId = (
  groups,
  pieceId,
  offset = [0, 0, 0]
) => {
  const group = getGroupFromPieceId(groups, pieceId)
  const piece = getPieceInGroup(group, pieceId)
  const gp = group.position || [0, 0, 0]
  const pp = piece.position || [0, 0, 0]
  const op = offset
  const result = sumArrays(gp, pp, op)
  return result
}

/**
 * Check the provided connection to see if the positions
 * are within the snap radius
 * @param {*} groups
 * @param {*} connection
 * @param {*} options
 */
const checkConnection = (
  groups,
  connection,
  options = { snapRadius: 4 }
) => {
  const pos1 = getPositionFromPieceId(
    groups,
    connection.p1.id,
    connection.p1.position
  )
  const pos2 = getPositionFromPieceId(
    groups,
    connection.p2.id,
    connection.p2.position
  )
  const distance = calcDistance(pos1, pos2)
  return distance < options.snapRadius * 2
}

/**
 * Checks all of the connections in a provided group ID
 * to see if they are within the snap radius
 * @param {object} connections
 * @param {object} groups
 * @param {string} groupId
 * @param {*} options
 */
export const getMatches = (
  connections,
  groups,
  groupId,
  options = { snapRadius: 4 }
) => {
  // get connections for all unconnected pieces in the group
  const group = getGroupFromId(groups, groupId)
  const connectionsToCheck = getConnectionsForGroup(
    connections,
    group
  )
  // check each of the connections for the dropped group
  const matches = connectionsToCheck.filter(c =>
    checkConnection(groups, c, options)
  )
  return matches
}

/**
 * Moves the dropped piece into it's parent group
 */
function makeConnection(match, groups, droppedId) {
  // get source group and point
  const sourceGroup = getGroupFromId(groups, droppedId)
  const sourcePieceIds = sourceGroup.pieces.map(p => p.id)
  const sourcePoint =
    sourcePieceIds.indexOf(match.p1.id) > -1
      ? match.p1
      : match.p2
  const sourcePiece = getPieceInGroup(
    sourceGroup,
    sourcePoint.id
  )

  // get destination group and point
  const destPoint =
    sourcePoint.id === match.p1.id ? match.p2 : match.p1
  const destGroup = getGroupFromPieceId(
    groups,
    destPoint.id
  )
  const destPiece = getPieceInGroup(destGroup, destPoint.id)

  // 1. normalize piece positions relative to source piece position
  const normalizedSourcePieces = sourceGroup.pieces.map(
    p => {
      const newPos = subtractArrays(
        p.position || [0, 0, 0],
        sourcePiece.position || [0, 0, 0]
      )
      // never change vertical position (index = 1)
      return {
        ...p,
        position: [newPos[0], p.position[1], newPos[2]]
      }
    }
  )

  // 2. calculate amount to translate pieces in source group
  const translateAmount = subtractArrays(
    destPoint.position,
    sourcePoint.position
  )

  // 3. translate all of the pieces in the source group
  const translatedPieces = normalizedSourcePieces.map(p => {
    const endPosition = sumArrays(
      p.position,
      destPiece.position || [0, 0, 0],
      translateAmount
    )
    return {
      ...p,
      position: [
        endPosition[0],
        p.position[1],
        endPosition[2]
      ]
    }
  })

  const newGroups = groups.map(g => {
    if (g.id === destGroup.id) {
      // 4. add all pieces from source group to destination
      g.pieces = [...g.pieces, ...translatedPieces]
    }
    if (g.id === sourceGroup.id) {
      // 5. empty source group
      g.pieces = []
      g.position = [0, 0, 0]
    }
    return g
  })

  return newGroups
}

/**
 * Connect the pieces defined by the provided connections
 * if they fall within the snap radius.
 * @param {*} connections
 * @param {*} groups
 * @param {*} groupId
 * @param {*} options
 */
function connectPieces(
  connection,
  connections,
  groups,
  groupId
) {
  // check if connection has been made already
  // if connection.p1.id and connection.p2.id are in the same group
  const g1 = getGroupFromPieceId(groups, connection.p1.id)
  const g2 = getGroupFromPieceId(groups, connection.p2.id)
  const alreadyMatched = g1.id === g2.id
  // get the updated groups once connection is made
  const newGroups = alreadyMatched
    ? groups
    : makeConnection(connection, groups, groupId)
  // remove the connection that has been made
  const newConnections = connections.filter(
    c =>
      !(
        c.p1.id === connection.p1.id &&
        c.p2.id === connection.p2.id
      )
  )
  // TODO: update connections that reference a piece in the source group
  // return the new pieces of state
  return {
    groups: newGroups,
    connections: newConnections
  }
}

const resetPieces = (active, resetState) => {
  const newState = {
    ...active,
    groups: active.groups.map(p => {
      const resetGroup = resetState.groups.find(
        rg => rg.id === p.id
      )
      p.position = resetGroup.position
      p.pieces = resetGroup.pieces
      return p
    }),
    connections: resetState.connections,
    position: resetState.position,
    rotation: resetState.rotation
  }
  return newState
}

/**
 * Returns the puzzle state with the group containing pieces
 * centered on the origin
 * @param {*} state
 */
const centerPieces = state => {
  // grab the group containing all the pieces
  const solvedGroup = state.groups.find(
    g => g.pieces.length > 0
  )
  // get the postion of the left-most piece
  const min = solvedGroup.pieces.reduce(
    (min, current) => [
      current.position[0] < min[0]
        ? current.position[0]
        : min[0],
      current.position[2] < min[1]
        ? current.position[2]
        : min[1]
    ],
    [99999, 99999]
  )
  // get the position of the right most piece
  const max = solvedGroup.pieces.reduce(
    (max, current) => [
      current.position[0] > max[0]
        ? current.position[0]
        : max[0],
      current.position[2] > max[1]
        ? current.position[2]
        : max[1]
    ],
    [-99999, -99999]
  )
  // get amount to offset the group
  const offsetX = (max[0] + min[0]) / 2
  const offsetY = (max[1] + min[1]) / 2
  // offset the group
  const newGroups = state.groups.map((g, i) => {
    const position = [-offsetX, g.position[1], -offsetY]
    return { ...g, position }
  })
  // return updated puzzle state
  return { ...state, groups: newGroups, complete: true }
}

/**
 * Reducer that moves the group corresponding to the
 * `groupId` to the provided `position`
 * @param {*} state
 * @param {*} groupId
 * @param {*} position
 */
const movePuzzleGroup = (state, groupId, position) => {
  // move the position of the group
  const newState1 = {
    groups: state.active.groups.map(p => {
      p.position = p.id === groupId ? position : p.position
      return p
    }),
    connections: state.active.connections
  }
  // get matches for connections within the snap radius
  const matches = getMatches(
    newState1.connections,
    newState1.groups,
    groupId,
    { snapRadius: state.snapRadius }
  )
  // return updated state if no matches
  if (!matches || matches.length === 0) {
    return { ...state.active, ...newState1 }
  }
  // make the first matched connection
  const newState2 = connectPieces(
    matches[0],
    newState1.connections,
    newState1.groups,
    groupId
  )
  // return the new state
  const updatedPuzzle = { ...state.active, ...newState2 }
  return updatedPuzzle.connections.length === 0
    ? centerPieces(updatedPuzzle)
    : updatedPuzzle
}

const isComplete = puzzle =>
  puzzle &&
  puzzle.groups.map(g => g.pieces.length).filter(g => g > 0)
    .length === 1

export {
  isComplete,
  sumArrays,
  connectPieces,
  movePuzzleGroup,
  centerPieces,
  getConnectionsForPieceId,
  resetPieces
}
